<script lang="ts">
const MIN_WIDTH = 300;
const MAX_WIDTH = 800;

export default { name: 'WidgetSidebar' };
</script>

<script setup lang="ts">
import { ref, computed, inject, watch, type Ref, type StyleValue } from 'vue';

import { useRouter } from '@router';
import { useStore } from '@store';
import { isDateish } from '@tools/type-guards';

import Icon from '@components/Icon.vue';
import Spacer from '@components/Spacer.vue';

import { faXmark } from '@icons/solid/faXmark';
import { faEnvelope } from '@icons/regular/faEnvelope';
import { faCheck } from '@icons/solid/faCheck';
import { faUser } from '@icons/solid/faUser';

import type { Organization } from '@models/organization';

import type { DayUsageInfo } from './LicenseUsageCalendarWidget.vue';

export interface DeleteInviteEvent {
  dateKey: string;
  inviteEmail: string;
  courseId: string;
  inviteId: string;
}

export interface RemoveStudentEvent {
  dateKey: string;
  inviteEmail: string;
  courseId: string;
  studentId: string;
}

export interface Emits {
  (event: 'close'): void;
  (event: 'delete-invite', data: DeleteInviteEvent): void;
  (event: 'remove-student', data: RemoveStudentEvent): void;
}

const emit = defineEmits<Emits>();

const router = useRouter();
const store = useStore();

const organizationId = inject('organizationId') as string;
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments, @typescript-eslint/no-redundant-type-constituents
const usageInfo = inject('selectedUsageInfo') as Ref<DayUsageInfo | null>;

const usedStudentLicenses = inject('usedStudentLicenses') as Ref<number>;
const usedInstructorLicenses = inject('usedInstructorLicenses') as Ref<number>;
const usedAdminLicenses = inject('usedAdminLicenses') as Ref<number>;

const organizationLicensingMethod = inject(
  'organizationLicensingMethod',
) as Ref<Organization['licensingMethod']> | null;

const sidebarInner = ref<HTMLDivElement | null>(null);

const isMobile = computed(() => store.state.displayMode === 'mobile');

// Sidebar width management
const manualSidebarWidth = ref(500);
const isResizing = ref(false);

const isAutoSiteLicenseOrganization = computed(() => {
  return organizationLicensingMethod?.value === 'auto';
});

const style = computed(() => {
  const rules: StyleValue = {};

  if (!isMobile.value) {
    rules.width = `${manualSidebarWidth.value}px`;
  }

  return rules;
});

const headerText = computed(() => {
  return isDateish(usageInfo.value?.date)
    ? new Intl.DateTimeFormat('en', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(usageInfo.value.date)
    : '';
});

const displayUsageInfo = computed(() => {
  const rows = [];

  // if manual license organization, show current usage info for organization.
  // Since usageInfo.value is the projected current day's info based on invites
  // and current licenses, it really only makes sense for auto site license
  // organizations.
  rows.push({
    key: 'students',
    label: 'Students',
    value: `${
      isAutoSiteLicenseOrganization.value &&
      usageInfo.value?.type === 'projected'
        ? usageInfo.value?.usedStudentLicenses
        : usedStudentLicenses.value
    } / ${usageInfo.value?.maxStudentLicenses}`,
  });

  rows.push({
    key: 'instructors',
    label: 'Instructors',
    value: `${
      isAutoSiteLicenseOrganization.value &&
      usageInfo.value?.type === 'projected'
        ? usageInfo.value?.usedInstructorLicenses
        : usedInstructorLicenses.value
    } / ${usageInfo.value?.maxInstructorLicenses}`,
  });

  rows.push({
    key: 'admins',
    label: 'Admins',
    value: `${
      isAutoSiteLicenseOrganization.value &&
      usageInfo.value?.type === 'projected'
        ? usageInfo.value?.usedAdminLicenses
        : usedAdminLicenses.value
    } / ${usageInfo.value?.maxAdminLicenses}`,
  });

  return rows;
});

const coursePanels = computed(() => {
  const panels = [];

  for (const course of usageInfo.value?.courses ?? []) {
    const members = [];

    for (const member of Object.values(course.members)) {
      const typeClass = member.studentId ? 'invite-accepted' : 'invite-pending';

      const title = member.studentId ? 'Invite Accepted' : 'Invite Pending';

      let statusIcon;

      if (usageInfo.value?.type === 'historical') {
        statusIcon = faUser;
      } else if (member.studentId) {
        statusIcon = faCheck;
      } else {
        statusIcon = faEnvelope;
      }

      const label = member.label ?? member.email;

      let action;

      if (
        usageInfo.value?.key &&
        usageInfo.value?.type === 'projected' &&
        member.courseId
      ) {
        const dateKey = usageInfo.value.key;
        const { email: inviteEmail, inviteId, courseId, studentId } = member;

        if (studentId) {
          action = {
            text: 'Remove',
            fn: () => {
              emit('remove-student', {
                dateKey,
                inviteEmail,
                courseId,
                studentId,
              });
            },
          };
        } else if (inviteId) {
          action = {
            text: 'Delete',
            fn: () => {
              emit('delete-invite', {
                dateKey,
                inviteEmail,
                courseId,
                inviteId,
              });
            },
          };
        }
      }

      members.push({
        key: member.email,
        typeClass,
        title,
        statusIcon,
        label,
        action,
      });
    }

    // sort by invite pending first, then by label
    members.sort((a, b) => {
      if (a.typeClass === 'invite-pending') {
        return -1;
      }
      return a.label.localeCompare(b.label);
    });

    panels.push({
      id: course.id,
      name: course.name,
      members,
    });
  }

  return panels;
});

const licenseUsageTitle = computed(() => {
  if (
    !isAutoSiteLicenseOrganization.value &&
    usageInfo.value?.type === 'projected'
  ) {
    return 'Current License Usage';
  }
  return 'Daily License Usage';
});

function startResize(event: MouseEvent) {
  isResizing.value = true;

  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';

  const startX = event.clientX;
  const startWidth = manualSidebarWidth.value;

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing.value) return;

    // Inverted because we're dragging from the left edge
    const deltaX = startX - e.clientX;

    const newWidth = Math.min(
      MAX_WIDTH,
      Math.max(MIN_WIDTH, startWidth + deltaX),
    );

    manualSidebarWidth.value = newWidth;
  };

  const handleMouseUp = () => {
    isResizing.value = false;

    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
}

watch(
  () => usageInfo.value?.key,
  () => {
    sidebarInner.value?.scrollTo({ top: 0 });
  },
);

function showCourseInfo(courseId: string) {
  void router.push({
    name: 'dashboardCourse',
    params: { organizationId, courseId },
  });
}
</script>

<template>
  <div class="widget-sidebar" v-bind="{ style }">
    <div
      v-if="!isMobile"
      :class="['resize-handle', { 'is-resizing': isResizing }]"
      title="Drag to resize sidebar"
      @mousedown="startResize"
    />

    <div ref="sidebarInner" class="sidebar-inner">
      <div class="sidebar-header">
        <Transition name="bounce">
          <div :key="headerText" class="header-content">
            <div class="h4 mb-0 mr-3">{{ headerText }}</div>

            <UsageTypeEmblem :type="usageInfo?.type" text />
          </div>
        </Transition>

        <button title="Close sidebar" @click="$emit('close')">
          <Icon :icon="faXmark" />
        </button>
      </div>

      <div class="sidebar-body">
        <!-- #region Section: License Usage -->

        <div class="sidebar-section">
          <div class="h5 m-0">
            {{ licenseUsageTitle }}
          </div>

          <div class="d-flex flex-column">
            <div
              v-for="row in displayUsageInfo"
              :key="row.key"
              class="d-flex align-items-center"
            >
              <div>{{ row.label }}</div>

              <Spacer type="dotted" />

              <div>{{ row.value }}</div>
            </div>
          </div>
        </div>

        <!-- #endregion Section: License Usage -->

        <!-- #region Section: Courses & Participants -->

        <div class="sidebar-section">
          <div class="h5 m-0">Courses & Participants</div>

          <div
            v-for="panel in coursePanels"
            :key="panel.id"
            class="sidebar-panel"
          >
            <div class="sidebar-panel-header">
              <div class="sidebar-panel-header-name">
                Course: {{ panel.name }}
              </div>

              <div
                v-if="usageInfo?.type === 'projected'"
                class="sidebar-panel-header-actions"
              >
                <b-button
                  variant="outline-secondary"
                  @click="showCourseInfo(panel.id)"
                >
                  More Info
                </b-button>
              </div>
            </div>

            <div class="member-list">
              <div
                v-for="member in panel.members"
                :key="member.key"
                :class="['member-list-item', member.typeClass]"
                :title="member.title"
              >
                <div class="member-status">
                  <Icon :icon="member.statusIcon" />
                </div>

                <div class="member-info">{{ member.label }}</div>

                <div v-if="member.action" class="member-actions">
                  <b-button variant="outline-danger" @click="member.action.fn">
                    {{ member.action.text }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- #endregion Section: Courses & Participants -->
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.widget-sidebar {
  position: relative;
  height: 100%;
  border-left: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  z-index: 1;

  @include app-theme-light {
    background-color: #d7d8e2;
  }

  @include app-theme-dark {
    background-color: #141519;
  }

  @include app-mobile {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border-left: none;
  }
}

.resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  z-index: 10;
  background-color: transparent;
  transition: background-color 0.2s;

  &:hover {
    background-color: #007bffcc;
  }

  &.is-resizing {
    background-color: #007bff;
  }
}

.sidebar-inner {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
}

.sidebar-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 2rem;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  width: 100%;
  min-height: 120px;
  padding: 2rem 4rem 2rem 2rem;
  border-bottom: 1px solid var(--border-color);

  button {
    position: absolute;
    right: 2rem;
    display: inline-block;
    justify-content: center;
    align-items: center;
    width: 2rem;
    height: 2rem;
    font-size: 1.2rem;
    line-height: 1em;
    padding: 0;
    background: none;
    color: var(--text-color);
    opacity: 1;
    transition: opacity 0.25s;

    &:hover {
      opacity: 0.5;
    }
  }
}

.header-content {
  display: flex;
  align-items: center;
}

.sidebar-body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: auto;
}

.sidebar-panel {
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);

  @include app-theme-light {
    background-color: #dcdeeb;
  }

  @include app-theme-dark {
    background-color: #111215;
  }
}

.sidebar-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem;
  border-bottom: 1px solid var(--border-color);
}

.sidebar-panel-header-name {
  overflow: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  margin-right: 1rem;
  flex-grow: 1;
  color: var(--heading-color);
}

.sidebar-panel-header-actions {
  flex-shrink: 0;

  .btn {
    width: 75px;
    font-size: 0.8rem;
    padding: 0.25rem 0;
    line-height: 1em;
  }
}

.member-list {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
}

.member-list-item {
  display: flex;
  align-items: center;
  font-size: 0.875em;
  font-weight: 400;
  padding: 0.5rem 1rem;
}

.member-status {
  width: 20px;
  height: 20px;
  font-size: 0.6rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 100%;
  border: 1px solid currentColor;

  .member-list-item.invite-pending & {
    color: #6c757d;
  }

  .member-list-item.invite-accepted & {
    color: #00a083;
  }
}

.member-info {
  flex-grow: 1;
  overflow: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  margin: 0 1rem;
}

.member-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;

  .btn {
    width: 75px;
    font-size: 0.8rem;
    padding: 0.25rem 0;
    line-height: 1em;
  }
}

.bounce-enter-active {
  animation: bounce-in 0.3s;
}

.bounce-leave-active {
  display: none;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }

  50% {
    transform: scale(1.15);
  }

  100% {
    transform: scale(1);
  }
}

.content-type-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;

  &.historical {
    color: var(--data-historical-color);
  }

  &.projected {
    color: var(--data-projected-color);
  }
}

.info-type-icon {
  width: 22.8px;
  height: 22.8px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 1rem;

  .content-type-label.historical & {
    background-color: var(--data-historical-color) !important;

    @include app-theme-light {
      color: #322e23;
    }

    @include app-theme-dark {
      color: #322e23;
    }
  }

  .content-type-label.projected & {
    @include app-theme-light {
      color: #1c2e34;
      background-color: var(--data-projected-color) !important;
    }

    @include app-theme-dark {
      color: #1c2e34;
      background-color: var(--data-projected-color) !important;
    }
  }
}
</style>
